// Keep in sync with: src/lib/flo.ts (web)
//
// FLO metric engine — Charge / Load / Flow. Pure computation functions plus
// thin Supabase loaders. Contract: docs/OLMPS_V8_SPEC.md, section "FLO
// engine". The PURE functions below (calcTaskPriorityIntended, computeLoad,
// computeCharge, computeFlow, and all their small helpers) must stay
// byte-identical between the mobile and web copies of this file — only the
// loader's environment (which `supabase` client is imported/passed in) can
// ever differ, and today it doesn't even do that, since both platforms use
// the same @supabase/supabase-js client shape.
//
// ─────────────────────────────────────────────────────────────────────────
// Load: uses `calcTaskPriorityIntended`, ported verbatim from
// apps/mobile/src/tools/NumaTool.tsx (the CORRECT priority formula — see
// that file's PARITY NOTE for why NUMA's own charts intentionally use a
// different, buggy formula for web bug-parity; FLO is a new surface and
// always uses the correct one, on both platforms).
//
// Charge: the manual's §6.1 master equation assumes wearable-sourced
// physiological signal. There is no wearable here, so this file implements
// the "operational bridge" the spec calls for: hour-classification blocks
// are synthesized from calendar_events + METIS agenda_items + THEMIS
// protocol_completions + scheduled/completed KYRS tasks. The exact
// classification heuristics (documented inline below, near
// `classifyCalendarBlock` / `classifyTaskBlock` / block builders) are an
// implementation choice within the spec's stated ambiguity — see the build
// report for a summary.
// ─────────────────────────────────────────────────────────────────────────

import type { SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// Shared value types (mirror the DB shapes used by NumaTool/KyrsTool/
// MetisTool/ThemisTool/KrnosTool — see those files for the canonical schema).
// ============================================================================

export interface FloGroup {
  id: string;
  groupNumber: number;
}

export interface FloValueCriterion {
  id: string;
  score: number;
}

export interface FloValueSetting {
  id: string;
  isActive: boolean;
  criteria: FloValueCriterion[];
}

/** Per-type drain multipliers, user calibration (`user_settings.flo_settings.drainMultipliers`). */
export interface FloDrainMultipliers {
  habit?: number;
  focused?: number;
  complex?: number;
}

export interface FloTaskLike {
  id: string;
  title: string;
  listId: string | null;
  category: string | null;
  groupId: string | null;
  sg1Id: string | null;
  sg2Id: string | null;
  valueSelections: Record<string, string> | null;
  defaultPriority: number | null;
  /** Free-text duration estimate (e.g. "30 min", "1 hour"). Used only for
   *  building an hour-classification block when the task is scheduled/done
   *  with a specific time — never affects the priority score. */
  etc: string | null;
  scheduled: string | null;
  done: boolean;
  unlisted: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  /** True when this task's list/category is linked to a TKNE (`designs`
   *  table) project doc — see `isProjectLinkedTask` below. */
  isProjectLinked: boolean;
}

// ============================================================================
// Priority score — ported verbatim from NumaTool.tsx's calcTaskPriorityIntended.
// Do not "fix" the quirk where the value-selections branch and the
// default-priority branch are mutually exclusive (the `else` overwrites
// `total` rather than adding to it) — that is the intended behavior this
// formula reproduces.
// ============================================================================

export function calcTaskPriorityIntended(
  task: Pick<FloTaskLike, 'groupId' | 'sg1Id' | 'sg2Id' | 'valueSelections' | 'defaultPriority' | 'scheduled'>,
  groups: FloGroup[],
  valueSettings: FloValueSetting[],
  unscheduledBonus: number
): number {
  let total = 0;
  const group = groups.find((g) => g.id === task.groupId);
  if (group) total += group.groupNumber;
  const sg1 = groups.find((g) => g.id === task.sg1Id);
  if (sg1) total += sg1.groupNumber;
  const sg2 = groups.find((g) => g.id === task.sg2Id);
  if (sg2) total += sg2.groupNumber;

  if (task.valueSelections && Object.keys(task.valueSelections).length > 0) {
    const activeValueSettings = valueSettings.filter((v) => v.isActive);
    for (const valueIdKey of Object.keys(task.valueSelections)) {
      const criterionId = task.valueSelections[valueIdKey];
      const valueSetting = activeValueSettings.find((v) => v.id === valueIdKey);
      if (valueSetting) {
        const criterion = valueSetting.criteria.find((c) => c.id === criterionId);
        if (criterion) total += criterion.score;
      }
    }
  } else if (task.defaultPriority !== undefined && task.defaultPriority !== null) {
    total = task.defaultPriority;
  }

  if (!task.scheduled) total += unscheduledBonus;
  return total;
}

// ============================================================================
// Load
// ============================================================================

export interface FloRockItem {
  id: string;
  title: string;
  priority: number;
  listId: string | null;
}

export interface LoadResult {
  total: number;
  rocks: FloRockItem[];
}

/**
 * Load = sum of intended-formula priority points across "open active" tasks:
 * not done, not unlisted (unlisted tasks are explicitly "excluded from...
 * scheduling" per KYRS — treated here as non-actionable and excluded from
 * Load the same way).
 */
export function computeLoad(
  tasks: FloTaskLike[],
  groups: FloGroup[],
  valueSettings: FloValueSetting[],
  unscheduledTaskPriorityBonus: number
): LoadResult {
  const openActive = tasks.filter((t) => !t.done && !t.unlisted);
  const scored = openActive.map((t) => ({
    id: t.id,
    title: t.title,
    listId: t.listId,
    priority: calcTaskPriorityIntended(t, groups, valueSettings, unscheduledTaskPriorityBonus),
  }));
  const total = scored.reduce((sum, s) => sum + s.priority, 0);
  const rocks = scored
    .slice()
    .sort((a, b) => b.priority - a.priority)
    .slice(0, 5);
  return { total, rocks };
}

// ============================================================================
// Charge
// ============================================================================

export type FloBlockKind = 'habit' | 'focused' | 'complex';

/** One hour-classification block for "today", in minutes since local midnight. */
export interface FloBlock {
  startMinutes: number;
  endMinutes: number;
  kind: FloBlockKind;
  source: 'calendar' | 'metis' | 'themis' | 'task';
}

export interface ChargeInputs {
  /** Today's synthesized hour-classification blocks (see loader block builders). */
  blocks: FloBlock[];
  /** Total open-task priority points (Load.total) — feeds the background-load drain. */
  openPriorityLoad: number;
  /** Reference "now" — defaults to `new Date()`. Also defines "today" (local midnight → now). */
  now?: Date;
  drainMultipliers?: FloDrainMultipliers;
  /** Wake time for today (NUMA 2.2): background drain starts here, not at
   *  midnight; before it, no drain for today. Default = 07:00 local today.
   *  Callers pass `wakeOf(now, game)` from the user's morningSessionTime. */
  wakeAt?: Date;
}

export type FloMode = 'premium' | 'throttled';

export interface ChargeResult {
  charge: number;
  mode: FloMode;
  /** Minutes projected until Charge crosses 8.0, at the day's average drain
   *  rate so far. Null when not draining, or already at/below 8.0. */
  minutesTo8: number | null;
}

const CHARGE_START = 9.75;
const CHARGE_MODE_THRESHOLD = 8.0;
const CHARGE_MIN = 0;
const CHARGE_MAX = 10;

const BASE_DRAIN_PER_HOUR: Record<FloBlockKind, number> = {
  habit: 0.4,
  focused: 1.0,
  complex: 2.0,
};

const BACKGROUND_DRAIN_PER_50_LOAD_PER_HOUR = 0.1;
const BACKGROUND_DRAIN_CAP_PER_HOUR = 0.5;

const SWITCHING_TAX_PER_TRANSITION = 0.2;
const SWITCHING_TAX_CAP = 1.0;

const CONCURRENCY_MULTIPLIER = 1.5;

const BREAK_GAP_MINUTES = 15;
const BREAK_RECOVERY = 0.25;

const DRAIN_MULTIPLIER_MIN = 0.75;
const DRAIN_MULTIPLIER_MAX = 1.25;

function clampDrainMultiplier(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 1;
  return Math.min(DRAIN_MULTIPLIER_MAX, Math.max(DRAIN_MULTIPLIER_MIN, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function blocksOverlap(a: FloBlock, b: FloBlock): boolean {
  return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/** Default wake (07:00 local) used when a caller does not pass the user's setting. */
export const DEFAULT_WAKE_HOUR = 7;
function defaultWake(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), DEFAULT_WAKE_HOUR, 0, 0, 0);
}

/**
 * Charge (0-10). Master equation per OLMPS_V8_SPEC.md "FLO engine", using the
 * operational bridge (no wearable): hour-classification blocks drain at a
 * fixed per-kind rate, with a concurrency multiplier for overlapping non-habit
 * blocks; a background drain proportional to open Load; a switching tax for
 * focused<->complex transitions; and a recovery credit for >=15min gaps
 * between blocks.
 */
export function computeCharge(inputs: ChargeInputs): ChargeResult {
  const now = inputs.now ?? new Date();
  const mult: Record<FloBlockKind, number> = {
    habit: clampDrainMultiplier(inputs.drainMultipliers?.habit),
    focused: clampDrainMultiplier(inputs.drainMultipliers?.focused),
    complex: clampDrainMultiplier(inputs.drainMultipliers?.complex),
  };

  const sorted = inputs.blocks
    .filter((b) => b.endMinutes > b.startMinutes)
    .slice()
    .sort((a, b) => a.startMinutes - b.startMinutes);

  // 1) Hour-classification drain, with ×1.5 concurrency when a non-habit
  // block overlaps at least one other non-habit block.
  let blockDrain = 0;
  for (const block of sorted) {
    const hours = (block.endMinutes - block.startMinutes) / 60;
    const rate = BASE_DRAIN_PER_HOUR[block.kind] * mult[block.kind];
    const hasNonHabitOverlap =
      block.kind !== 'habit' &&
      sorted.some((other) => other !== block && other.kind !== 'habit' && blocksOverlap(block, other));
    const concurrency = hasNonHabitOverlap ? CONCURRENCY_MULTIPLIER : 1;
    blockDrain += hours * rate * concurrency;
  }

  // 2) Background Load drain: -0.1/h per 50 open priority points, capped at -0.5/h.
  const wake = inputs.wakeAt ?? defaultWake(now);
  const hoursElapsed = Math.max(0, (now.getTime() - wake.getTime()) / 3_600_000);
  const backgroundRatePerHour = Math.min(
    BACKGROUND_DRAIN_CAP_PER_HOUR,
    BACKGROUND_DRAIN_PER_50_LOAD_PER_HOUR * (inputs.openPriorityLoad / 50)
  );
  const backgroundDrain = backgroundRatePerHour * hoursElapsed;

  // 3) Switching tax: -0.2 per focused<->complex transition, capped at -1.0
  // total. habit<->anything is free (spec: "habit<->habit free"; we extend
  // that to "any transition touching a habit block is free", since habit
  // blocks represent routine/background activity, not a context switch).
  let switchingTax = 0;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    const bothSwitchable = (a.kind === 'focused' || a.kind === 'complex') && (b.kind === 'focused' || b.kind === 'complex');
    if (bothSwitchable && a.kind !== b.kind) {
      switchingTax += SWITCHING_TAX_PER_TRANSITION;
    }
  }
  switchingTax = Math.min(SWITCHING_TAX_CAP, switchingTax);

  // 4) Breaks: +0.25 per >=15min gap between consecutive blocks.
  let breaksRecovery = 0;
  for (let i = 0; i < sorted.length - 1; i++) {
    const gap = sorted[i + 1].startMinutes - sorted[i].endMinutes;
    if (gap >= BREAK_GAP_MINUTES) breaksRecovery += BREAK_RECOVERY;
  }

  const netDelta = -blockDrain - backgroundDrain - switchingTax + breaksRecovery;
  const charge = clamp(CHARGE_START + netDelta, CHARGE_MIN, CHARGE_MAX);
  const mode: FloMode = charge > CHARGE_MODE_THRESHOLD ? 'premium' : 'throttled';

  // Coach line: linear projection to 8.0 at the day's average drain rate so
  // far (net change since the 9.75 reset, spread over hours elapsed today).
  let minutesTo8: number | null = null;
  if (charge > CHARGE_MODE_THRESHOLD && hoursElapsed > 0) {
    const netSoFar = charge - CHARGE_START;
    if (netSoFar < 0) {
      const ratePerHour = -netSoFar / hoursElapsed;
      if (ratePerHour > 0) {
        minutesTo8 = Math.round(((charge - CHARGE_MODE_THRESHOLD) / ratePerHour) * 60);
      }
    }
  }

  return { charge: round2(charge), mode, minutesTo8 };
}

// ============================================================================
// Green bands (Book 2.3) — the game's measure: Charge >= chargeMin,
// Load <= baseline x loadMaxOfBaseline, Flow >= flowMin. (The 8.0 `mode`
// above is an internal engine throttle only, not a game line.)
// ============================================================================

export interface GreenBandsLike {
  chargeMin: number;
  loadMaxOfBaseline: number;
  flowMin: number;
}

export interface GreenStatus {
  charge: boolean;
  load: boolean;
  flow: boolean;
  all: boolean;
}

export function greenStatus(
  v: { charge: number; load: number; flow: number },
  bands: GreenBandsLike,
  baseline: number
): GreenStatus {
  const charge = v.charge >= bands.chargeMin;
  const load = v.load <= baseline * bands.loadMaxOfBaseline;
  const flow = v.flow >= bands.flowMin;
  return { charge, load, flow, all: charge && load && flow };
}

/** Day-close helper for the sessions agent: true when NUMA's three greens all
 *  hold (award AWARD.NUMA_THREE_GREENS = +2). Pure; does not mark anything. */
export function numaThreeGreens(
  v: { charge: number; load: number; flow: number },
  bands: GreenBandsLike,
  baseline: number
): boolean {
  return greenStatus(v, bands, baseline).all;
}

// ============================================================================
// Flow
// ============================================================================

export interface FlowCreatedItem {
  id: string;
  createdAt: string;
}

export interface FlowCompletedItem {
  id: string;
  createdAt: string;
  completedAt: string;
  priority: number;
  isProjectLinked: boolean;
}

export type FloBand = 'blue' | 'green' | 'orange' | 'red';

export interface FlowResult {
  score: number;
  band: FloBand;
  cycle: number;
  exchange: number;
  steadiness: number;
  latency: number;
}

const LOW_PRIORITY_LATENCY_THRESHOLD = 2;
const LATENCY_QUICK_DAYS = 3;
const LATENCY_PROJECT_DAYS = 30;
const LATENCY_GENERAL_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysBetween(aIso: string, bIso: string): number {
  const a = new Date(aIso).getTime();
  const b = new Date(bIso).getTime();
  return Math.max(0, (b - a) / MS_PER_DAY);
}

function latencyTargetDays(item: FlowCompletedItem): number {
  if (item.isProjectLinked) return LATENCY_PROJECT_DAYS;
  if (item.priority <= LOW_PRIORITY_LATENCY_THRESHOLD) return LATENCY_QUICK_DAYS;
  return LATENCY_GENERAL_DAYS;
}

function bandFor(score: number): FloBand {
  if (score >= 100) return 'blue';
  if (score >= 80) return 'green';
  if (score >= 60) return 'orange';
  return 'red';
}

/**
 * Flow (0-100+). Weights 40/30/20/10 on Cycle/Exchange/Steadiness/Latency.
 * `completedItems`/`createdItems` should already be scoped to the window
 * (default 28 days) by the caller/loader.
 */
export function computeFlow(
  completedItems: FlowCompletedItem[],
  createdItems: FlowCreatedItem[],
  windowDays = 28
): FlowResult {
  // Cycle: 100/(1+avgOpenDays/7) over items completed in the window.
  let cycle = 0;
  if (completedItems.length > 0) {
    const avgOpenDays =
      completedItems.reduce((sum, item) => sum + daysBetween(item.createdAt, item.completedAt), 0) /
      completedItems.length;
    cycle = 100 / (1 + avgOpenDays / 7);
  }

  // Exchange: (completions/day ÷ creations/day) × 100, capped at 150.
  const completionsPerDay = completedItems.length / windowDays;
  const creationsPerDay = createdItems.length / windowDays;
  let exchange: number;
  if (creationsPerDay > 0) {
    exchange = Math.min(150, (completionsPerDay / creationsPerDay) * 100);
  } else {
    exchange = completionsPerDay > 0 ? 150 : 0;
  }

  // Steadiness: 100/(1+CV) of daily completion counts across the window.
  const dailyCounts = new Array(windowDays).fill(0) as number[];
  const now = Date.now();
  for (const item of completedItems) {
    const completedAt = new Date(item.completedAt).getTime();
    const daysAgo = Math.floor((now - completedAt) / MS_PER_DAY);
    if (daysAgo >= 0 && daysAgo < windowDays) {
      dailyCounts[windowDays - 1 - daysAgo] += 1;
    }
  }
  const mean = dailyCounts.reduce((a, b) => a + b, 0) / windowDays;
  let steadiness = 0;
  if (mean > 0) {
    const variance = dailyCounts.reduce((sum, c) => sum + (c - mean) ** 2, 0) / windowDays;
    const stdev = Math.sqrt(variance);
    const cv = stdev / mean;
    steadiness = 100 / (1 + cv);
  }

  // Latency: % closed within their target window (quick 3d / general 7d / project 30d).
  let latency = 0;
  if (completedItems.length > 0) {
    const withinTarget = completedItems.filter(
      (item) => daysBetween(item.createdAt, item.completedAt) <= latencyTargetDays(item)
    ).length;
    latency = (withinTarget / completedItems.length) * 100;
  }

  const score = 0.4 * cycle + 0.3 * exchange + 0.2 * steadiness + 0.1 * latency;

  return { score: round2(score), band: bandFor(score), cycle: round2(cycle), exchange: round2(exchange), steadiness: round2(steadiness), latency: round2(latency) };
}

// ============================================================================
// Snapshot
// ============================================================================

export interface FloSnapshot {
  charge: number;
  mode: FloMode;
  minutesTo8: number | null;
  load: number;
  rocks: FloRockItem[];
  flow: FlowResult;
}

// ============================================================================
// Loaders (Supabase). Thin — all computation lives in the pure functions
// above. Schema reference: tasks (KyrsTool.tsx/NumaTool.tsx), calendar_events
// (KrnosTool.tsx), agenda_items (MetisTool.tsx), protocol_completions
// (ThemisTool.tsx), designs (TkneTool.tsx — sync_list_id/sync_category_*),
// user_settings (groups/value_settings/unscheduled_task_priority_bonus, and
// the not-guaranteed-to-exist flo_settings jsonb column).
// ============================================================================

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function dateKeyOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseDateKeyLocal(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

function isSameLocalDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isBeforeLocalDate(a: Date, b: Date): boolean {
  const ka = a.getFullYear() * 10000 + (a.getMonth() + 1) * 100 + a.getDate();
  const kb = b.getFullYear() * 10000 + (b.getMonth() + 1) * 100 + b.getDate();
  return ka < kb;
}

/** Ported from KrnosTool.tsx's isEventOccurringOnDate. */
function isCalendarEventOnDate(item: RawCalendarEvent, checkDate: Date): boolean {
  const eventStart = parseDateKeyLocal(item.date);
  if (item.exceptions && item.exceptions.includes(dateKeyOf(checkDate))) return false;
  if (item.end_date) {
    const seriesEnd = parseDateKeyLocal(item.end_date);
    if (isBeforeLocalDate(seriesEnd, checkDate)) return false;
  }
  if (isBeforeLocalDate(checkDate, eventStart) && !isSameLocalDate(checkDate, eventStart)) return false;
  if (!item.repeat || item.repeat === 'none') return isSameLocalDate(eventStart, checkDate);
  if (isBeforeLocalDate(checkDate, eventStart)) return false;
  if (item.repeat === 'daily') return true;
  if (item.repeat === 'weekly') return checkDate.getDay() === eventStart.getDay();
  if (item.repeat === 'monthly') return checkDate.getDate() === eventStart.getDate();
  if (item.repeat === 'yearly') {
    return checkDate.getMonth() === eventStart.getMonth() && checkDate.getDate() === eventStart.getDate();
  }
  return false;
}

function parseTimeToMinutes24h(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mins = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(mins)) return null;
  return h * 60 + mins;
}

/** Parses the display-format ("H:MM AM/PM") produced by MetisTool's normalizeTime. */
function parseTimeToMinutes12h(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(text.trim());
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) h += 12;
  return h * 60 + Number(m[2]);
}

function parseEtcMinutes(etc: string | null | undefined): number | null {
  if (!etc) return null;
  const m = /(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours|m|min|mins|minute|minutes)?/i.exec(etc.trim());
  if (!m) return null;
  const value = Number(m[1]);
  if (Number.isNaN(value)) return null;
  const unit = (m[2] || 'm').toLowerCase();
  return unit.startsWith('h') ? value * 60 : value;
}

/**
 * Parses a KYRS `scheduled` value ("yyyy-MM-dd HH:mm", "yyyy-MM-dd",
 * "today"/"tomorrow"/"yesterday") and returns minutes-since-midnight *only*
 * when it (a) resolves to today's date and (b) carries an explicit time
 * component — otherwise null (no time block can be formed).
 */
function parseScheduledMinutesIfToday(scheduled: string | null, today: Date): number | null {
  if (!scheduled) return null;
  const s = scheduled.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!isSameLocalDate(d, today)) return null;
    return Number(m[4]) * 60 + Number(m[5]);
  }
  return null;
}

const DEFAULT_TASK_BLOCK_MINUTES = 30;

interface RawCalendarEvent {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  repeat: string | null;
  end_date: string | null;
  exceptions: string[] | null;
}

interface RawAgendaItem {
  item_id: string;
  time: string | null;
  duration: string | null;
}

interface RawThemisCompletion {
  id: string;
  started_at: string;
  total_duration_seconds: number | null;
}

interface RawDesign {
  id: string;
  sync_list_id: string | null;
  sync_category_list_id: string | null;
  sync_category_name: string | null;
}

interface RawTaskRow {
  id: string;
  title: string | null;
  list_id: string | null;
  category: string | null;
  group_id: string | null;
  sg1_id: string | null;
  sg2_id: string | null;
  value_selections: Record<string, string> | null;
  default_priority: number | null;
  etc: string | null;
  scheduled: string | null;
  done: boolean | null;
  unlisted: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

function isProjectLinkedTask(task: RawTaskRow, designs: RawDesign[]): boolean {
  return designs.some((d) => {
    if (d.sync_list_id && d.sync_list_id === task.list_id) return true;
    if (d.sync_category_list_id && d.sync_category_list_id === task.list_id && d.sync_category_name && d.sync_category_name === task.category) {
      return true;
    }
    return false;
  });
}

/** Classifies a calendar block: recurring events are the day's routine
 *  backbone ('habit'); one-off events default to 'focused' (calendar events
 *  carry no priority/due-date signal to promote them to 'complex'). */
function classifyCalendarBlock(item: RawCalendarEvent): FloBlockKind {
  return item.repeat && item.repeat !== 'none' ? 'habit' : 'focused';
}

/** Classifies a scheduled/completed-today KYRS task block: TKNE-project-linked
 *  tasks = 'complex' (the heavy allotment — TKNE projects auto-schedule into
 *  the calendar, so they carry the day's heavy work), everything else =
 *  'focused'. Tasks have no habit flag, so this source never contributes
 *  'habit'. */
function classifyTaskBlock(task: FloTaskLike): FloBlockKind {
  return task.isProjectLinked ? 'complex' : 'focused';
}

export interface FloInputs {
  now: Date;
  tasks: FloTaskLike[];
  calendarEvents: RawCalendarEvent[];
  agendaItemsToday: RawAgendaItem[];
  themisCompletionsToday: RawThemisCompletion[];
  groups: FloGroup[];
  valueSettings: FloValueSetting[];
  unscheduledTaskPriorityBonus: number;
  drainMultipliers?: FloDrainMultipliers;
  /** Tasks created or completed within the Flow window (default 28d), for
   *  computeFlow's completed/created arrays. Subset of `tasks`. */
  flowWindowDays: number;
}

/** Fetches everything computeLoad/computeCharge/computeFlow need for one user. */
export async function loadFloInputs(supabase: SupabaseClient, userId: string, flowWindowDays = 28): Promise<FloInputs> {
  const now = new Date();
  const todayKey = dateKeyOf(now);
  const todayStart = startOfLocalDay(now);
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

  const [settingsRes, tasksRes, designsRes, calendarRes, agendaRes, themisRes] = await Promise.all([
    // Read defensively with select('*') — do NOT assume flo_settings exists.
    supabase.from('user_settings').select('*').eq('user_id', userId).maybeSingle(),
    supabase
      .from('tasks')
      .select(
        'id, title, list_id, category, group_id, sg1_id, sg2_id, value_selections, default_priority, etc, scheduled, done, unlisted, created_at, updated_at'
      )
      .eq('user_id', userId),
    supabase.from('designs').select('id, sync_list_id, sync_category_list_id, sync_category_name').eq('user_id', userId),
    supabase.from('calendar_events').select('*').eq('user_id', userId),
    supabase.from('agenda_items').select('item_id, time, duration').eq('user_id', userId).eq('date', todayKey),
    supabase
      .from('protocol_completions')
      .select('id, started_at, total_duration_seconds')
      .eq('user_id', userId)
      .gte('started_at', todayStart.toISOString())
      .lt('started_at', todayEnd.toISOString()),
  ]);

  const settingsRow = (settingsRes.data ?? null) as Record<string, unknown> | null;
  const groups = (Array.isArray(settingsRow?.groups) ? (settingsRow!.groups as FloGroup[]) : []).map((g) => ({
    id: g.id,
    groupNumber: g.groupNumber,
  }));
  const valueSettings = (Array.isArray(settingsRow?.value_settings) ? (settingsRow!.value_settings as FloValueSetting[]) : []);
  const unscheduledTaskPriorityBonus =
    typeof settingsRow?.unscheduled_task_priority_bonus === 'number' ? (settingsRow!.unscheduled_task_priority_bonus as number) : 0;
  const floSettings = (settingsRow?.flo_settings ?? null) as { drainMultipliers?: FloDrainMultipliers } | null;

  const designs = (designsRes.data ?? []) as RawDesign[];
  const rawTasks = (tasksRes.data ?? []) as RawTaskRow[];

  const tasks: FloTaskLike[] = rawTasks.map((t) => ({
    id: t.id,
    title: t.title ?? '',
    listId: t.list_id,
    category: t.category,
    groupId: t.group_id,
    sg1Id: t.sg1_id,
    sg2Id: t.sg2_id,
    valueSelections: t.value_selections,
    defaultPriority: t.default_priority,
    etc: t.etc,
    scheduled: t.scheduled,
    done: !!t.done,
    unlisted: !!t.unlisted,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    isProjectLinked: isProjectLinkedTask(t, designs),
  }));

  return {
    now,
    tasks,
    calendarEvents: (calendarRes.data ?? []) as RawCalendarEvent[],
    agendaItemsToday: (agendaRes.data ?? []) as RawAgendaItem[],
    themisCompletionsToday: (themisRes.data ?? []) as RawThemisCompletion[],
    groups,
    valueSettings,
    unscheduledTaskPriorityBonus,
    drainMultipliers: floSettings?.drainMultipliers,
    flowWindowDays,
  };
}

/** Builds today's hour-classification blocks from the four data sources. */
function buildTodaysBlocks(inputs: FloInputs): FloBlock[] {
  const blocks: FloBlock[] = [];
  const today = inputs.now;

  for (const ev of inputs.calendarEvents) {
    if (!isCalendarEventOnDate(ev, today)) continue;
    const start = parseTimeToMinutes24h(ev.start_time);
    const end = parseTimeToMinutes24h(ev.end_time);
    if (start === null || end === null || end <= start) continue;
    blocks.push({ startMinutes: start, endMinutes: end, kind: classifyCalendarBlock(ev), source: 'calendar' });
  }

  for (const item of inputs.agendaItemsToday) {
    const start = parseTimeToMinutes12h(item.time);
    const durationMinutes = parseEtcMinutes(item.duration ?? null);
    if (start === null || !durationMinutes || durationMinutes <= 0) continue;
    blocks.push({ startMinutes: start, endMinutes: start + durationMinutes, kind: 'focused', source: 'metis' });
  }

  for (const run of inputs.themisCompletionsToday) {
    const started = new Date(run.started_at);
    const startMinutes = started.getHours() * 60 + started.getMinutes();
    const durationMinutes = (run.total_duration_seconds ?? 0) / 60;
    if (durationMinutes <= 0) continue;
    blocks.push({ startMinutes, endMinutes: startMinutes + durationMinutes, kind: 'habit', source: 'themis' });
  }

  // Done tasks are deliberately NOT skipped: completing a task no longer clears
  // `scheduled`, so work finished today still occupies its block and Charge
  // keeps draining for it ("scheduled or completed", book [11812]).
  for (const task of inputs.tasks) {
    const startMinutes = parseScheduledMinutesIfToday(task.scheduled, today);
    if (startMinutes === null) continue;
    const durationMinutes = parseEtcMinutes(task.etc) ?? DEFAULT_TASK_BLOCK_MINUTES;
    blocks.push({
      startMinutes,
      endMinutes: startMinutes + durationMinutes,
      kind: classifyTaskBlock(task),
      source: 'task',
    });
  }

  return blocks;
}

/** Orchestrates the loaders + the three pure computations for one user. */
export async function getFloSnapshot(
  supabase: SupabaseClient,
  userId: string,
  flowWindowDays = 28,
  wakeAt?: Date
): Promise<FloSnapshot> {
  const inputs = await loadFloInputs(supabase, userId, flowWindowDays);

  const load = computeLoad(inputs.tasks, inputs.groups, inputs.valueSettings, inputs.unscheduledTaskPriorityBonus);

  const blocks = buildTodaysBlocks(inputs);
  const chargeResult = computeCharge({
    blocks,
    openPriorityLoad: load.total,
    now: inputs.now,
    drainMultipliers: inputs.drainMultipliers,
    wakeAt,
  });

  const cutoff = inputs.now.getTime() - inputs.flowWindowDays * MS_PER_DAY;
  const createdItems: FlowCreatedItem[] = inputs.tasks
    .filter((t) => t.createdAt && new Date(t.createdAt).getTime() >= cutoff)
    .map((t) => ({ id: t.id, createdAt: t.createdAt as string }));
  // `tasks` has no completed_at column, so a done task's updated_at is used
  // as a completedAt proxy (this is imprecise if a done task is edited again
  // afterwards, but it's the only signal the schema provides).
  const completedItems: FlowCompletedItem[] = inputs.tasks
    .filter((t) => t.done && t.createdAt && t.updatedAt && new Date(t.updatedAt).getTime() >= cutoff)
    .map((t) => ({
      id: t.id,
      createdAt: t.createdAt as string,
      completedAt: t.updatedAt as string,
      priority: calcTaskPriorityIntended(t, inputs.groups, inputs.valueSettings, inputs.unscheduledTaskPriorityBonus),
      isProjectLinked: t.isProjectLinked,
    }));
  const flow = computeFlow(completedItems, createdItems, inputs.flowWindowDays);

  return {
    charge: chargeResult.charge,
    mode: chargeResult.mode,
    minutesTo8: chargeResult.minutesTo8,
    load: load.total,
    rocks: load.rocks,
    flow,
  };
}
